import { useEffect, useState, useCallback, Suspense } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { CustomerBottomNav } from '@/components/customer/CustomerBottomNav';
import { CustomerSidebar } from '@/components/customer/CustomerSidebar';
import { NotificationCenter } from '@/components/customer/NotificationCenter';
import { useIsMobile } from '@/hooks/use-mobile';
import { Loader2, RefreshCw, LogOut, Building2, Clock } from 'lucide-react';
import paynovusLogo from '@/assets/paynovus-logo-horizontal.svg';
import { Button } from '@/components/ui/button';
import { useCustomerSessionTimeout } from '@/hooks/useCustomerSessionTimeout';
import SessionTimeoutCountdown from '@/components/SessionTimeoutCountdown';
import { useApplyTheme } from '@/hooks/useApplyTheme';
import { AUTH_ONBOARDING_RESUME_KEY } from '@/constants/onboardingResume';

export default function CustomerLayout() {
  const { user, profile, loading, profileLoaded, profilePending, signOut } = useCustomerAuth();
  const { showCountdown, countdownSeconds, resetTimer } = useCustomerSessionTimeout();
  useApplyTheme();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [waitedTooLong, setWaitedTooLong] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const shouldResumeOnboarding = !loading && profileLoaded && !!user && !profile && !profilePending && Boolean(sessionStorage.getItem(AUTH_ONBOARDING_RESUME_KEY));

  useEffect(() => {
    if (!loading && !user) {
      navigate('/app/login', { replace: true });
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (shouldResumeOnboarding) {
      navigate('/app/onboard', { replace: true });
    }
  }, [shouldResumeOnboarding, navigate]);

  // If profile is null after loading, check for type mismatch first
  useEffect(() => {
    if (!loading && profileLoaded && user && !profile) {
      // Check if this was a login type mismatch (e.g. selected Business but no Business profile)
      const mismatch = localStorage.getItem('pn_login_type_mismatch');
      if (mismatch) {
        localStorage.removeItem('pn_login_type_mismatch');
        const label = mismatch === 'corporate' ? 'Business' : 'Personal';
        signOut().then(() => {
          navigate('/app/login', { replace: true, state: { error: `No ${label} account found for this email. Please select the correct account type or contact support.` } });
        });
        return;
      }
      const timer = setTimeout(() => setWaitedTooLong(true), 8000);
      return () => clearTimeout(timer);
    }
    setWaitedTooLong(false);
  }, [loading, profileLoaded, user, profile, navigate, signOut]);

  const handleRetry = useCallback(() => {
    setRetrying(true);
    setWaitedTooLong(false);
    // Force a full page reload to re-bootstrap auth + profile
    window.location.reload();
  }, []);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/app/login', { replace: true });
  }, [signOut, navigate]);

  if (loading || !profileLoaded) {
    return (
      <div className="min-h-screen-safe bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <img src={paynovusLogo} alt="PayNovus" className="h-10 w-auto opacity-60 mb-2" />
          <Loader2 className="w-7 h-7 text-accent animate-spin" />
          <p className="text-muted-foreground text-sm">Loading your account...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  if (shouldResumeOnboarding) {
    return (
      <div className="min-h-screen-safe bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center px-4">
          <img src={paynovusLogo} alt="PayNovus" className="h-10 w-auto opacity-60 mb-2" />
          <Loader2 className="w-7 h-7 text-accent animate-spin" />
          <p className="text-muted-foreground text-sm">Restoring your application...</p>
        </div>
      </div>
    );
  }

  if (profilePending) {
    return (
      <div className="min-h-screen-safe bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <img src={paynovusLogo} alt="PayNovus" className="h-10 w-auto mx-auto mb-6 opacity-60" />
          <div className="w-16 h-16 rounded-full bg-warning/10 border border-warning/20 flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-warning" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Application Under Review</h2>
          <p className="text-muted-foreground text-sm mb-6">
            Your application has been received and is currently being reviewed by our team. You'll receive an email once it's been approved.
          </p>
          <Button variant="outline" onClick={handleSignOut} className="w-full">
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen-safe bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <img src={paynovusLogo} alt="PayNovus" className="h-10 w-auto mx-auto mb-6 opacity-60" />
          {!waitedTooLong ? (
            <>
              <Loader2 className="w-8 h-8 text-accent animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-bold text-foreground mb-2">Setting up your account…</h2>
              <p className="text-muted-foreground text-sm">
                Your account is being prepared. This usually takes a few moments.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold text-foreground mb-2">Account not ready yet</h2>
              <p className="text-muted-foreground text-sm mb-6">
                Your account may still be under review, or there was an issue loading your profile. You can retry or sign out and try again later.
              </p>
              <div className="flex flex-col gap-3">
                <Button onClick={handleRetry} disabled={retrying} className="w-full">
                  <RefreshCw className={`w-4 h-4 mr-2 ${retrying ? 'animate-spin' : ''}`} />
                  {retrying ? 'Retrying…' : 'Retry'}
                </Button>
                <Button variant="outline" onClick={handleSignOut} className="w-full">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
    {showCountdown && <SessionTimeoutCountdown secondsLeft={countdownSeconds} onStayLoggedIn={resetTimer} />}
    <div className="min-h-screen-safe bg-background flex">
      {!isMobile && <CustomerSidebar />}
      
      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        {/* Mobile top bar */}
        {isMobile && (
          <header className="sticky top-0 z-40 bg-card/90 backdrop-blur-xl border-b border-border/60 px-4 py-3 flex items-center justify-between safe-area-inset-top shadow-xs">
            <div className="flex items-center gap-2 min-w-0">
              <img src={paynovusLogo} alt="PayNovus" className="h-7 w-auto shrink-0" />
              {profile?.client_type === 'corporate' && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 border border-blue-500/25 shrink-0">
                  Business
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <NotificationCenter />
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ${
                profile?.client_type === 'corporate'
                  ? 'bg-blue-500/15 text-blue-400'
                  : 'bg-accent/15 text-accent'
              }`}>
                {profile?.client_type === 'corporate'
                  ? <Building2 className="w-4 h-4" />
                  : (profile?.full_name || 'U')[0].toUpperCase()
                }
              </div>
            </div>
          </header>
        )}
        
        <main className={`flex-1 overflow-y-auto overflow-x-hidden w-full ${isMobile ? 'pb-24' : ''}`} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
          <Suspense fallback={
            <div className="flex flex-col gap-4 p-4 sm:p-6 animate-pulse">
              <div className="h-8 w-48 max-w-full rounded-xl bg-secondary/60" />
              <div className="h-32 w-full rounded-2xl bg-secondary/40" />
              <div className="h-24 w-full rounded-2xl bg-secondary/30" />
              <div className="h-24 w-full rounded-2xl bg-secondary/20" />
            </div>
          }>
            <Outlet />
          </Suspense>
        </main>
      </div>
      
      {isMobile && <CustomerBottomNav />}
    </div>
    </>
  );
}
